import React, { useEffect, useState } from 'react';

import { Card, List } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getBookCover } from '@libs/NwUtils';

import { onUpdatePlaylists } from '@hooks/API/Contact/UseContactPlaylists';
import { useWebsites } from '@hooks/API/UseWebsites';
import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import {
    BookItemTitle,
    getBookLinkedWebsites,
} from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/BookItem';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import AvatarBg from '@components/Gui/AvatarBg';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import PlayListInfoDrawer from '@components/MediaDrawer/PlayListInfoDrawer';
import PlayListPreviewDrawer from '@components/MediaDrawer/PlayListPreviewDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/** Breaks out of NwCard flex column so Card.Grid floats lay out in two columns. */
const MediaBooksGridSection = styled.div`
    display: block;
    width: 100%;
    min-width: 0;
`;

const GridCard = styled(Card.Grid)`
    float: left;
    width: 50%;
    box-sizing: border-box;
    height: 170px;
    background: white;
    padding: 12px 24px;
    @media only screen and (max-width: 720px) {
        width: 100%;
    }
`;

const BooksGridHost = styled(Card)`
    width: 100%;
    min-width: 0;
    background: transparent;
    margin: 0 !important;

    && .ant-card-body {
        display: block;
        flex: none;
        padding: 0;
        margin: 0;
        &::after {
            content: '';
            display: table;
            clear: both;
        }
    }

    .preview-button {
        border-right: none;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
    .info-button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
`;

const BookAuthorText = styled.div`
    font-size: 0.6em;
    text-transform: uppercase;
    margin-top: 0.3em;
`;

const BOOK_ROW_AVATAR_SIZE = 96;

const MediaBooks = ({ documentID, contactID }) => {
    const [bookPreview, setBookPreview] = useState(null);
    const [bookInfo, setBookInfo] = useState(null);
    const [isBookDrawer, setIsBookDrawer] = useState(false);
    const [playlists, setPlaylists] = useState([]);
    const breakpoints = useNwBreakPoints();
    const { data: websiteData } = useWebsites();
    const adjustTagUsageCount = useAdjustTagUsageCount();

    const getPlaylists = async () => {
        const playlistsdata = await Axios.get(`/documents/playlists/${documentID}`);
        setPlaylists(playlistsdata);
    };

    useEffect(() => {
        if (documentID) {
            getPlaylists();
        }
    }, [documentID]);

    const showBookPreview = (book) => {
        setBookPreview(book);
        setIsBookDrawer(true);
    };

    const showBookInfo = (book) => {
        setBookInfo(book);
        setIsBookDrawer(true);
    };

    const onDeleteBook = async (book) => {
        try {
            await Axios.delete(`/playlists/${book.ID}`);
            // il book eliminato non usa piu' i suoi tag
            (book.Tags || []).forEach((tag) => adjustTagUsageCount(tag.ID, -1));
            onUpdatePlaylists(contactID);
            getPlaylists();
        } catch (error) {
            console.error('error on deleting book', error);
        }
    };

    const renderBookRow = (book) => {
        const linkedSections = getBookLinkedWebsites(book, websiteData || []);
        return (
            <List.Item className="ant-list-item-top" style={{ padding: '8px 0' }}>
                <List.Item.Meta
                    onClick={() => {
                        showBookPreview(book);
                    }}
                    style={{ minHeight: BOOK_ROW_AVATAR_SIZE, overflow: 'hidden' }}
                    avatar={
                        <AvatarBg
                            size={BOOK_ROW_AVATAR_SIZE}
                            src={getBookCover(book)}
                            onClick={() => showBookPreview(book)}
                            style={{ cursor: 'pointer' }}
                        />
                    }
                    title={
                        <BookItemTitle
                            book={book}
                            linkedSections={linkedSections}
                            exist={false}
                            sortableDragging={false}
                            embeddedInSortableRow={false}
                            onTitleClick={() => showBookPreview(book)}
                            actions={
                                <FlexContainer>
                                    <NwButton
                                        icon={byPrefixAndName.fasl['images-user']}
                                        onClick={() => showBookPreview(book)}
                                        className="preview-button"
                                    />
                                    <NwButton
                                        icon={byPrefixAndName.fasl['circle-info']}
                                        onClick={() => showBookInfo(book)}
                                        className="info-button"
                                    />
                                </FlexContainer>
                            }
                        />
                    }
                    description={
                        breakpoints !== 1 && (
                            <FlexContainer column justifyContent="space-between" alignItems="flex-start">
                                <BookAuthorText>
                                    <CreatedBy object={book} short textonly />
                                </BookAuthorText>
                            </FlexContainer>
                        )
                    }
                />
            </List.Item>
        );
    };

    return (
        <NwCard icon={byPrefixAndName.fasl['books']} title="Books" padding="no">
            {playlists.length > 0 ? (
                <MediaBooksGridSection>
                    <BooksGridHost bordered={false}>
                        {playlists.map((book, index) => (
                            <GridCard key={book.ID ?? index}>
                                <div>{renderBookRow(book)}</div>
                            </GridCard>
                        ))}
                    </BooksGridHost>
                </MediaBooksGridSection>
            ) : null}
            {/* {renderContent()} */}
            {isBookDrawer && bookPreview && (
                <PlayListPreviewDrawer
                    playlist={bookPreview}
                    contactID={contactID}
                    onClose={() => {
                        setIsBookDrawer(false);
                        setBookPreview();
                    }}
                />
            )}
            {isBookDrawer && bookInfo && (
                <PlayListInfoDrawer
                    playlist={bookInfo}
                    contactID={contactID}
                    onEditPlaylist={() => {}}
                    onClose={() => {
                        setIsBookDrawer(false);
                        setBookInfo();
                    }}
                    onUpdatePlaylists={() => {
                        onUpdatePlaylists(contactID);
                        getPlaylists();
                    }}
                    onDeletePlaylists={onDeleteBook}
                    onPublishBook={() => {}}
                />
            )}
        </NwCard>
    );
};

export default MediaBooks;
